import React, { useEffect, useState, useCallback } from 'react';
import { useNavigate } from 'react-router-dom';
import axios from 'axios';
import { FileText, MessageSquare, ChevronRight, Calendar } from 'lucide-react';
import { BACKEND_URL } from '../config/api';
import { workerTypeLabel } from '../utils/workerType';

/**
 * Recent Quote Requests Panel — quick-resume widget for the employer
 * dashboard. Lists the most recent quote-request conversations only;
 * day-rate offer flows are intentionally excluded.
 *
 * Backend: GET /api/employer/quote-requests/recent
 */
const STATUS_BADGE = {
  unread: { text: 'New reply', cls: 'bg-rose-100 text-rose-700 border-rose-200' },
  replied: { text: 'Replied', cls: 'bg-emerald-100 text-emerald-700 border-emerald-200' },
  awaiting_reply: { text: 'Awaiting reply', cls: 'bg-amber-100 text-amber-800 border-amber-200' },
};

function formatRelative(iso) {
  if (!iso) return '';
  const then = new Date(iso);
  const now = new Date();
  const diffMs = now - then;
  const minutes = Math.floor(diffMs / 60000);
  if (minutes < 1) return 'just now';
  if (minutes < 60) return `${minutes}m ago`;
  const hours = Math.floor(minutes / 60);
  if (hours < 24) return `${hours}h ago`;
  const days = Math.floor(hours / 24);
  if (days < 7) return `${days}d ago`;
  return then.toLocaleDateString('en-GB', { day: 'numeric', month: 'short' });
}

function formatTargetDate(iso) {
  if (!iso) return null;
  const d = new Date(iso);
  if (Number.isNaN(d.getTime())) return iso;
  return d.toLocaleDateString('en-GB', { day: 'numeric', month: 'short', year: 'numeric' });
}

export default function RecentQuoteRequestsPanel() {
  const navigate = useNavigate();
  const [items, setItems] = useState([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(false);

  const fetchItems = useCallback(async () => {
    setLoading(true);
    setError(false);
    try {
      const res = await axios.get(`${BACKEND_URL}/api/employer/quote-requests/recent`, {
        withCredentials: true,
      });
      setItems(res.data?.quote_requests || []);
    } catch (e) {
      // Silent on auth/no-employer (handled by parent dashboard); flag others.
      if (e.response && e.response.status !== 401 && e.response.status !== 403) {
        setError(true);
      }
      setItems([]);
    } finally {
      setLoading(false);
    }
  }, []);

  useEffect(() => {
    fetchItems();
  }, [fetchItems]);

  const handleOpenChat = (item) => {
    navigate('/messages', {
      state: {
        recipient: {
          user_id: item.tradesperson_id,
          name: item.tradesperson_name,
          user_type: 'tradesperson',
          worker_type: item.worker_type,
        },
      },
    });
  };

  // Render nothing when there are no quote-request conversations — the
  // dashboard already has plenty of empty-state copy elsewhere and we
  // don't want to clutter day-rate-only employers' dashboards.
  if (loading) {
    return (
      <div className="mt-8" data-testid="recent-quote-requests-panel">
        <h3 className="text-lg font-semibold text-slate-900 mb-4 flex items-center gap-2">
          <FileText className="h-5 w-5 text-amber-500" />
          Recent Quote Requests
        </h3>
        <div className="bg-white rounded-xl border border-slate-200 p-6 text-center text-slate-400 text-sm">
          Loading…
        </div>
      </div>
    );
  }

  if (error) {
    return (
      <div className="mt-8" data-testid="recent-quote-requests-panel">
        <h3 className="text-lg font-semibold text-slate-900 mb-4 flex items-center gap-2">
          <FileText className="h-5 w-5 text-amber-500" />
          Recent Quote Requests
        </h3>
        <div className="bg-white rounded-xl border border-rose-100 p-4 text-sm text-rose-700">
          Could not load your quote requests right now. Refresh to try again.
        </div>
      </div>
    );
  }

  if (!items.length) return null;

  return (
    <div className="mt-8" data-testid="recent-quote-requests-panel">
      <h3 className="text-lg font-semibold text-slate-900 mb-4 flex items-center gap-2">
        <FileText className="h-5 w-5 text-amber-500" />
        Recent Quote Requests
        <span className="text-sm font-normal text-slate-500">({items.length})</span>
      </h3>

      <div className="grid grid-cols-1 md:grid-cols-2 gap-3">
        {items.map((item) => {
          const badge = STATUS_BADGE[item.status] || STATUS_BADGE.awaiting_reply;
          const wtLabel = workerTypeLabel(item.worker_type);
          return (
            <div
              key={item.quote_request_id || item.tradesperson_id}
              className="bg-white border border-slate-200 rounded-xl p-4 flex flex-col gap-2 hover:border-amber-300 transition-colors"
              data-testid={`quote-request-card-${item.tradesperson_id}`}
            >
              <div className="flex items-start justify-between gap-3">
                <div className="min-w-0 flex-1">
                  <p className="text-sm font-semibold text-slate-900 truncate" data-testid="qr-card-name">
                    {item.tradesperson_name || 'Tradesperson'}
                  </p>
                  <p className="text-xs text-slate-500 flex flex-wrap items-center gap-1.5">
                    {item.trade && <span data-testid="qr-card-trade">{item.trade}</span>}
                    {item.trade && wtLabel && <span aria-hidden>·</span>}
                    {wtLabel && (
                      <span
                        className="inline-flex items-center px-1.5 py-0.5 rounded bg-slate-100 text-slate-600 text-[11px]"
                        data-testid="qr-card-worker-type"
                      >
                        {wtLabel}
                      </span>
                    )}
                  </p>
                </div>
                <span
                  className={`inline-flex items-center px-2 py-0.5 rounded-full border text-[11px] font-semibold whitespace-nowrap ${badge.cls}`}
                  data-testid={`qr-card-status-${item.status}`}
                >
                  {badge.text}
                  {item.unread_count > 0 && item.status === 'unread' && (
                    <span className="ml-1.5 bg-rose-600 text-white rounded-full w-4 h-4 inline-flex items-center justify-center text-[10px] font-bold">
                      {item.unread_count}
                    </span>
                  )}
                </span>
              </div>

              {item.title && (
                <p className="text-sm font-medium text-slate-800 line-clamp-1" data-testid="qr-card-title">
                  {item.title}
                </p>
              )}

              {item.target_start_date && (
                <p className="text-xs text-slate-600 flex items-center gap-1" data-testid="qr-card-target-date">
                  <Calendar className="h-3.5 w-3.5 text-slate-400" />
                  Target start: {formatTargetDate(item.target_start_date)}
                </p>
              )}

              {item.last_message_preview && (
                <p className="text-xs text-slate-500 line-clamp-2 italic" data-testid="qr-card-preview">
                  {item.last_message_from_partner ? '↩ ' : '→ '}
                  {item.last_message_preview}
                </p>
              )}

              <div className="flex items-center justify-between pt-1.5 mt-auto">
                <span className="text-[11px] text-slate-400" data-testid="qr-card-last-activity">
                  {formatRelative(item.last_activity_at)}
                </span>
                <button
                  type="button"
                  onClick={() => handleOpenChat(item)}
                  className="inline-flex items-center gap-1 text-sm font-semibold text-orange-600 hover:text-orange-700"
                  data-testid={`qr-card-open-chat-${item.tradesperson_id}`}
                >
                  <MessageSquare className="h-4 w-4" />
                  Open Chat
                  <ChevronRight className="h-4 w-4" />
                </button>
              </div>
            </div>
          );
        })}
      </div>
    </div>
  );
}
